import React from 'react';
import PropTypes from 'prop-types';

import {Skeleton, TableRow} from "@mui/material";
import {styled} from "@mui/material/styles";

import ListingTableCell from "components/Product/Table/ListingTableCell";
import {greyishBlue50, greyishBlue100} from "assets/jss/main";

import {sessionStore} from "utils/sessionStore";
import {Authorizations, hasAuthorization} from "utils/authorizations";

OrderRowsSkeleton.propTypes = {
    rows: PropTypes.number,
};

const StyledBar = styled(Skeleton, {shouldForwardProp: (prop) => 'dark' !== prop})(({dark}) => ({
    transform: 'none',
    borderRadius: '4px',
    backgroundColor: dark ? greyishBlue100 : greyishBlue50,
    // left-to-right shimmer of the wave animation, as a white shine; MUI
    // delays the wave by 0.5s — with a fast API the skeleton would be gone
    // before the first sweep even starts
    '&::after': {
        background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent)',
        animationDelay: '0s',
        animationDuration: '1.2s'
    }
}));

const Column = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
});

const Row = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
});

const PillsRow = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
});

const Bar = (props) => <StyledBar animation="wave" {...props}/>;

// Mirrors OrderRow's 8 columns (order infos, customer, zone & type, total,
// statuses, tracking numbers, runner, notes) — approximates each column's
// shape, not its exact content.
function SkeletonRow() {
    return (
        <TableRow>
            <ListingTableCell>
                <Column sx={{width: '120px'}}>
                    <Bar dark height={16}/>
                    <Bar width={80} height={14}/>
                </Column>
                <PillsRow sx={{marginTop: '8px'}}>
                    <Bar width={60} height={20}/>
                </PillsRow>
            </ListingTableCell>
            <ListingTableCell>
                <Column sx={{width: '140px'}}>
                    <Bar dark height={16}/>
                    <Bar width={100} height={14}/>
                </Column>
            </ListingTableCell>
            <ListingTableCell>
                <Bar dark width={90} height={24} sx={{borderRadius: '8px', marginBottom: '8px'}}/>
                <Column sx={{width: '120px'}}>
                    <Bar height={14}/>
                    <Bar height={14}/>
                </Column>
            </ListingTableCell>
            <ListingTableCell>
                <Bar dark width={70} height={16}/>
            </ListingTableCell>
            <ListingTableCell>
                <PillsRow>
                    <Bar dark width={72} height={20}/>
                    <Bar width={64} height={20}/>
                </PillsRow>
            </ListingTableCell>
            <ListingTableCell>
                <Column sx={{width: '112px'}}>
                    <Bar height={16}/>
                    <Bar height={16}/>
                </Column>
            </ListingTableCell>
            {!hasAuthorization(sessionStore.get('authorizations'), Authorizations.PICKING_RETAILER) && (
                <ListingTableCell>
                    <Row>
                        <Bar dark width={32} height={32} sx={{borderRadius: '50%'}}/>
                        <Bar width={64} height={16}/>
                    </Row>
                </ListingTableCell>
            )}
            <ListingTableCell>
                <Column sx={{alignItems: 'center'}}>
                    <Bar dark width={24} height={24} sx={{borderRadius: '8px'}}/>
                    <Bar width={24} height={24} sx={{borderRadius: '8px'}}/>
                </Column>
            </ListingTableCell>
        </TableRow>
    );
}

// first-load placeholder of the OMS order list
function OrderRowsSkeleton({rows = 5}) {
    return (
        <>
            {Array.from({length: rows}, (_, index) => (
                <SkeletonRow key={index}/>
            ))}
        </>
    );
}

export default OrderRowsSkeleton;
